import React, { useEffect, useRef } from 'react';
import SignalField from './SignalField';
import { useTheme } from '../context/ThemeContext';

// Full-page boot splash: a "signals converging into a core" field (or, for a
// destination that asks for it, a spinning radar wheel + a bespoke tagline), shown
// edge-to-edge as the loading transition. It zooms in, holds, then zooms through and fades to
// reveal the app underneath. FOLLOWS THE USER'S THEME (gold in dark, blue in light). Click to skip.
//
// NO-FLASH HANDOFF: the navigation (`onReveal`) fires WHILE the backdrop is still fully opaque
// (~52% in), so the destination page mounts UNDER the splash and the back-half crossfade reveals
// the NEW page — never the old one. `onDone` (animation end / click) only unmounts the splash.
export default function SplashScreen({ onDone, onReveal, durationMs = 4200, tagline = '', spinner = false, cap = false }) {
  const { isDark } = useTheme();
  const reduce = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const ZOOM_MS = durationMs;              // total on-screen time
  const FADE_MS = Math.round(durationMs * 0.55);   // reduced-motion variant

  // Match the dashboard: gold accent in dark, blue in light.
  const accent = isDark ? '#c9a96e' : '#3b82f6';
  const bg = isDark
    ? 'radial-gradient(120% 120% at 50% 45%, #10141c 0%, #0a0d13 70%, #07090d 100%)'
    : 'radial-gradient(120% 120% at 50% 45%, #ffffff 0%, #f5f8fc 70%, #eef2f7 100%)';
  const brand = isDark ? '#eef1f6' : '#1f2733';
  const caption = isDark ? '#6b7688' : '#8a97a8';

  // Fire the navigation under cover, before the backdrop starts fading (52%). This is what
  // kills the "old page flashes before the new one" glitch. Guarded so it runs exactly once
  // even if the user clicks to skip first.
  // Typed one letter at a time, straight through the line — the last letter of a word is
  // followed by the first of the next, so it reads as one continuous hand rather than words
  // arriving in blocks.
  //
  // The pace is DERIVED, not fixed: the whole line must finish typing before the splash
  // starts fading at 52%, so the step is whatever fits between the start and 45%. A longer
  // sentence types faster rather than being cut off half-said, and the floor keeps a short
  // one from flickering past.
  const WORDS = String(tagline || '').split(' ').filter(Boolean);
  const CHARS = WORDS.join('').length || 1;
  const START = Math.round(durationMs * 0.08);
  const step = Math.max(28, Math.round((durationMs * 0.45 - START) / Math.max(1, CHARS - 1)));
  // A running index across the whole line, so word breaks do not restart the count.
  let charN = -1;
  const nextDelay = () => { charN += 1; return START + step * charN; };

  const revealedRef = useRef(false);
  const doReveal = () => { if (!revealedRef.current) { revealedRef.current = true; onReveal && onReveal(); } };
  useEffect(() => {
    const at = reduce ? Math.round(FADE_MS * 0.5) : Math.round(ZOOM_MS * 0.5);
    const t = setTimeout(doReveal, at);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Click / animation-end → make sure we've navigated, THEN unmount the splash.
  const finish = () => { doReveal(); onDone && onDone(); };

  return (
    <div
      className="ros-splash"
      role="presentation"
      onClick={finish}
      onAnimationEnd={(e) => { if (e.target === e.currentTarget) finish(); }}
      style={{ background: bg }}
    >
      <style>{`
        .ros-splash {
          position: fixed; inset: 0; z-index: 9999;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          animation: rosSplashBackdrop ${reduce ? FADE_MS : ZOOM_MS}ms ease forwards;
          cursor: pointer;
        }
        .ros-splash-stage {
          display: flex; flex-direction: column; align-items: center; gap: 10px;
          transform-origin: center; will-change: transform, opacity;
          animation: ${reduce ? `rosSplashFade ${FADE_MS}ms ease forwards`
                              : `rosSplashZoom ${ZOOM_MS}ms cubic-bezier(0.22,0.61,0.36,1) forwards`};
        }
        .ros-splash-brand {
          font-family: Georgia, 'Times New Roman', serif;
          font-size: clamp(20px, 3.4vw, 30px); letter-spacing: 0.02em; color: ${brand};
        }
        .ros-splash-brand span { color: ${accent}; }
        .ros-splash-field { position: relative; width: min(80vmin, 560px); height: min(66vmin, 460px); }
        .ros-splash-cap {
          font-family: ui-monospace, Menlo, monospace; font-size: 11px;
          letter-spacing: 0.34em; text-transform: uppercase; color: ${caption};
        }
        /* bespoke tagline (e.g. Research OS): serif, refined, wider reach than the mono cap */
        .ros-splash-tag {
          font-family: Georgia, 'Times New Roman', serif; font-style: italic;
          font-size: clamp(19px, 3vw, 30px); letter-spacing: 0.005em;
          color: ${isDark ? '#e8dcc0' : '#3a4453'};
          text-align: center; max-width: 26ch; line-height: 1.35; margin-top: 4px;
        }
        /* Maghaweer radar wheel — concentric rings + a rotating conic sweep + a spinning tick
           ring. Ties to the Research OS scope/radar identity; pure CSS, GPU-cheap. */
        .ros-radar { position: relative; width: min(82vmin, 460px); height: min(82vmin, 460px);
          display: grid; place-items: center; }
        .ros-radar-ring { position: absolute; border-radius: 50%;
          border: 1px solid ${isDark ? 'rgba(201,169,110,0.28)' : 'rgba(59,130,246,0.28)'}; }
        .ros-radar-r1 { inset: 0; } .ros-radar-r2 { inset: 16%; } .ros-radar-r3 { inset: 34%; }
        .ros-radar-cross { position: absolute; inset: 0; }
        .ros-radar-cross::before, .ros-radar-cross::after {
          content: ''; position: absolute; background: ${isDark ? 'rgba(201,169,110,0.20)' : 'rgba(59,130,246,0.20)'}; }
        .ros-radar-cross::before { left: 50%; top: 6%; bottom: 6%; width: 1px; transform: translateX(-0.5px); }
        .ros-radar-cross::after  { top: 50%; left: 6%; right: 6%; height: 1px; transform: translateY(-0.5px); }
        .ros-radar-sweep { position: absolute; inset: 0; border-radius: 50%;
          background: conic-gradient(from 0deg, transparent 0deg, transparent 255deg,
            ${accent}22 300deg, ${accent}66 348deg, ${accent}aa 360deg);
          animation: rosSweep 1.8s linear infinite; }
        .ros-radar-ticks { position: absolute; inset: -3%; border-radius: 50%;
          border: 1px dashed ${isDark ? 'rgba(201,169,110,0.22)' : 'rgba(59,130,246,0.22)'};
          animation: rosSweepRev 9s linear infinite; }
        .ros-radar-core { width: 13px; height: 13px; border-radius: 50%; background: ${accent};
          box-shadow: 0 0 22px ${accent}; z-index: 1; }
        /* The word is a plain box that holds its letters together for wrapping; the
           animation lives on the letters. */
        .ros-word { display: inline-block; white-space: nowrap; }
        .ros-word + .ros-word { margin-left: 0.32em; }
        .ros-char {
          display: inline-block; opacity: 0;
          animation: rosCharIn 0.34s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
        }
        @keyframes rosCharIn {
          from { opacity: 0; transform: translateY(0.16em); }
          to   { opacity: 1; transform: none; }
        }
        @keyframes rosSweep { to { transform: rotate(360deg); } }
        @keyframes rosSweepRev { to { transform: rotate(-360deg); } }
        @media (prefers-reduced-motion: reduce) {
          .ros-radar-sweep, .ros-radar-ticks { animation: none; }
          /* The whole line at once — typing is motion carrying no information. */
          .ros-char { animation: none; opacity: 1; }
        }
        /* zoom in, hold, then a slow gentle push-forward + fade so the destination page
           emerges through it (a dissolve/transition, not a hard cut). */
        @keyframes rosSplashZoom {
          0%   { transform: scale(0.82); opacity: 0; }
          14%  { transform: scale(1.00); opacity: 1; }
          52%  { transform: scale(1.015); opacity: 1; }
          100% { transform: scale(1.10); opacity: 0; }
        }
        @keyframes rosSplashFade {
          0% { opacity: 0; } 14% { opacity: 1; } 55% { opacity: 1; } 100% { opacity: 0; }
        }
        /* backdrop dissolves over the whole back half → crossfades into the app */
        @keyframes rosSplashBackdrop {
          0% { opacity: 1; } 52% { opacity: 1; } 100% { opacity: 0; }
        }
      `}</style>

      <div className="ros-splash-stage">
        <div className="ros-splash-brand">Maghaweer<span>.ai</span></div>
        {spinner ? (
          <div className="ros-radar" aria-hidden="true">
            <div className="ros-radar-ticks" />
            <div className="ros-radar-ring ros-radar-r1" />
            <div className="ros-radar-ring ros-radar-r2" />
            <div className="ros-radar-ring ros-radar-r3" />
            <div className="ros-radar-cross" />
            <div className="ros-radar-sweep" />
            <div className="ros-radar-core" />
          </div>
        ) : (
          <div className="ros-splash-field"><SignalField accent={accent} /></div>
        )}
        {/* Word by word, so the line is READ rather than just seen. Each word is its own
            span with a stepped delay; they are laid out in normal flow first and only then
            animated, so the line never reflows as they arrive and nothing shifts under the
            reader's eye. The last word lands before the splash starts fading — the timing
            below is derived from durationMs rather than fixed, so a shorter splash tightens
            the stagger instead of cutting the sentence off. */}
        {tagline ? (
          /* The line is announced once, whole. The per-character spans are decoration and
             are hidden from assistive tech — read out letter by letter it would be noise. */
          <div className={cap ? 'ros-splash-cap' : 'ros-splash-tag'} aria-label={tagline}>
            {WORDS.map((word, i) => (
              /* Word-level spans keep the wrap intact: characters alone would let a line
                 break fall inside a word. */
              <span key={i} className="ros-word" aria-hidden="true">
                {word.split('').map((ch, j) => (
                  <span key={j} className="ros-char" style={{ animationDelay: `${nextDelay()}ms` }}>
                    {ch}
                  </span>
                ))}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
